import { ApiSet, BaseResponse, IndexApiSet, useIndexApi, usePatchApi, usePostApi, useShowApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { SendHistory, SendHistoryForm, SendHistorySearchForm } from 'entities/send_history'

type SendHistoriesResponse = PagingResponse & {
    results: SendHistory[]
}

export function useFetchSendHistoriesApi(searchForm?: Form<SendHistorySearchForm>): IndexApiSet<SendHistoriesResponse> & { execute: () => void } {
    const apiPath = 'send_histories/'
    const api = useIndexApi<SendHistoriesResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object.page || 1, perPage: searchForm?.object.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

type SendHistoryResponse = BaseResponse & SendHistory

export function useFetchSendHistoryApi(): ApiSet<SendHistoryResponse> & {
    execute: (id: number) => void
} {
    const api = useShowApi<SendHistoryResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `send_histories/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export function usePostSendHistoryApi(): ApiSet<BaseResponse> & { execute: (form: Form<SendHistoryForm>) => void } {
    const apiSet = usePostApi<BaseResponse, SendHistoryForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<SendHistoryForm>) => {
        const apiPath = `send_histories/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePatchSendHistoryApi(): ApiSet<BaseResponse> & { execute: (id: number, form: Form<SendHistoryForm>) => void } {
    const apiSet = usePatchApi<BaseResponse, SendHistoryForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number, form: Form<SendHistoryForm>) => {
        const apiPath = `send_histories/${id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useCompleteSendHistoryApi(): ApiSet<BaseResponse> & { execute: (id: number, form: Form<SendHistoryForm>) => void } {
    const apiSet = usePatchApi<BaseResponse, SendHistoryForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (id: number, form: Form<SendHistoryForm>) => {
        const apiPath = `send_histories/${id}/complete/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}
